import { Box, Link, List, ListItem, Typography } from '@mui/material';
import type { Author } from '@repo/supergraph/base/types';
import { Badge } from '../../Logo/Badge';
import { StructuredContentRenderer } from '../../StructuredContentRenderer';
import { AuthorAvatar } from '../AuthorAvatar';
import { SocialMedia } from '../SocialMedia';
import { BadgeIcon } from './Icons/BadgeIcon';

const READ_FULL_PROFILE = 'See full bio';

type Props = Pick<Author, 'avatarUrls' | 'name' | 'link' | 'socialMediaLinks' | 'userPage'>;

export function AuthorTooltipContent({
  avatarUrls,
  name,
  link,
  socialMediaLinks,
  userPage,
}: Props) {
  const userExperiences = userPage?.experience
    ?.split(',')
    .map(experience => {
      const trimmed = experience.trim();
      return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
    })
    .filter(Boolean);

  return (
    <Box
      data-testid="author-tooltip-content"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pb: 3,
        gap: 2,
        flexWrap: 'wrap',
        textWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          gap: 3,
        }}
      >
        {avatarUrls?.[0].url ? (
          <AuthorAvatar sizeVariant="extraLarge" src={avatarUrls[0].url} alt={name} />
        ) : (
          <Box
            sx={{
              width: 55,
              height: 55,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: '1px solid white',
              bgcolor: 'grey.100',
              borderRadius: '50%',
            }}
          >
            <Badge />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="textBold"
            sx={{
              color: 'text.primary',
            }}
          >
            {name}
          </Typography>
          {userPage?.jobTitle && (
            <Typography
              variant="textExtraSmall"
              sx={{
                color: 'text.primary',
              }}
            >
              {userPage?.jobTitle}
            </Typography>
          )}
        </Box>
      </Box>
      {userExperiences && userExperiences.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <BadgeIcon />
            <Typography
              variant="textSmallBold"
              sx={{
                color: 'text.primary',
                lineHeight: 1,
              }}
            >
              Expertise
            </Typography>
          </Box>
          <List
            sx={{
              listStyleType: 'none',
              paddingLeft: '0.5rem',
              paddingY: 0,
            }}
          >
            {userExperiences.map(experience => {
              return (
                <ListItem
                  key={experience}
                  sx={{
                    display: 'list-item',
                    padding: '3px 0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '1.5rem', color: 'grey.300', lineHeight: 0.8 }}>
                      •
                    </Typography>
                    <Typography variant="textSmall" sx={{ color: 'grey.600', lineHeight: 1 }}>
                      {experience}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      <Box
        sx={{
          letterSpacing: '.03rem',
          lineHeight: 1.5,
          maxHeight: '3.75rem',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'grey.600',
          mt: 1,
          WebkitLineClamp: '3',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          '& p': { fontSize: '11px' },
        }}
      >
        {userPage?.bio && <StructuredContentRenderer doc={userPage.bio} />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          mt: 3.5,
          justifyContent: 'space-between',
        }}
      >
        {link && (
          <Link
            variant="textSmall"
            href={link}
            sx={{
              color: 'secondary.main',
              justifySelf: 'flex-end',
            }}
            aria-label={`${READ_FULL_PROFILE} for ${name}`}
          >
            <Typography
              variant="textSmallBold"
              sx={{
                color: 'secondary.main',
              }}
            >
              {READ_FULL_PROFILE}
            </Typography>
          </Link>
        )}
        {socialMediaLinks && socialMediaLinks.length > 0 && (
          <List
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              paddingY: 0,
            }}
          >
            {socialMediaLinks.map(link => (
              <ListItem key={link.url} sx={{ padding: 0 }}>
                <SocialMedia iconOnly {...link} aria-label={`Visit ${name} on ${link.type}`} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}
